import React, { useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { ManageAccounts } from '@mui/icons-material';

import {
  useDevZugangListe,
  useDevZugangSetzen,
} from '../../hooks/reactQuerys/DevZugangQueries';

const fehlerText = (exception: unknown, rueckfall: string): string =>
  exception instanceof Error && exception.message
    ? exception.message.replace(/^\d{3} [^:]+: /, '')
    : rueckfall;

/**
 * Entwickler-Zugang je Benutzer — nur hier zu setzen, nicht in der
 * Benutzerverwaltung. Testfunktionen und Freischaltungen (KI-Anbindung,
 * Word-Plugin, Health Check, GwG-Prüfung) vergibt jeweils ihre Unterseite
 * oben im Admin-Hub, nicht diese Tabelle.
 */
export const DevZugangVerwaltung: React.FC = () => {
  const { liste, isLoading, error } = useDevZugangListe();
  const { setzen, isPending } = useDevZugangSetzen();
  const [suche, setSuche] = useState('');
  const [fehler, setFehler] = useState<string | null>(null);

  if (isLoading) {
    return <CircularProgress size={24} />;
  }
  if (error || !liste) {
    return (
      <Alert severity='error'>
        Die Benutzerliste konnte nicht geladen werden.
      </Alert>
    );
  }

  async function schalten(idUser: number, isDev: boolean) {
    setFehler(null);
    try {
      await setzen({ idUser, isDev });
    } catch (exception) {
      setFehler(fehlerText(exception, 'Speichern fehlgeschlagen.'));
    }
  }

  const filter = suche.trim().toLowerCase();
  const benutzer = liste.Benutzer.filter(
    b =>
      filter === '' ||
      b.Name.toLowerCase().includes(filter) ||
      (b.Login ?? '').toLowerCase().includes(filter)
  );

  return (
    <Paper variant='outlined' sx={{ p: 2 }}>
      <Stack spacing={1.5}>
        <Stack direction='row' spacing={1} alignItems='center'>
          <ManageAccounts color='primary' />
          <Typography variant='subtitle1' fontWeight={600} sx={{ flexGrow: 1 }}>
            Entwickler-Zugang
          </Typography>
          <TextField
            size='small'
            placeholder='Benutzer suchen …'
            value={suche}
            onChange={e => setSuche(e.target.value)}
          />
        </Stack>
        <Typography variant='body2' color='text.secondary'>
          <b>Dev</b>: sieht und bearbeitet diese Seite samt Unterseiten und
          vergibt diesen Schalter. Er lässt sich nur hier setzen, nicht in der
          Benutzerverwaltung. Änderungen wirken beim nächsten Laden der Seite
          des betroffenen Benutzers. Testfunktionen und Freischaltungen
          (KI-Anbindung, Word-Plugin, Health Check, GwG-Prüfung) vergibt die
          jeweilige Unterseite oben.
        </Typography>
        {fehler !== null && <Alert severity='error'>{fehler}</Alert>}

        <Box sx={{ overflowX: 'auto' }}>
          <Table size='small'>
            <TableHead>
              <TableRow>
                <TableCell>Benutzer</TableCell>
                <TableCell>Login</TableCell>
                <TableCell align='center'>Dev</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {benutzer.map(b => {
                const selbst = b.idUser === liste.idUserSelbst;
                return (
                  <TableRow key={b.idUser} hover>
                    <TableCell>
                      <Stack direction='row' spacing={1} alignItems='center'>
                        <span>{b.Name || `Benutzer ${b.idUser}`}</span>
                        {selbst && <Chip size='small' label='Sie' />}
                        {b.isBlocked && (
                          <Chip size='small' color='default' label='gesperrt' />
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell>{b.Login}</TableCell>
                    <TableCell align='center'>
                      <Tooltip
                        title={
                          selbst && b.isDev
                            ? 'Den eigenen Zugang kann man nicht selbst entziehen.'
                            : ''
                        }
                      >
                        <span>
                          <Switch
                            size='small'
                            checked={b.isDev}
                            disabled={isPending || (selbst && b.isDev)}
                            onChange={(_, an) => void schalten(b.idUser, an)}
                          />
                        </span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Box>
      </Stack>
    </Paper>
  );
};
